<script setup>
// The week strip over a booking day grid, shared by the member /book page and the admin calendar (X47; X54 ruling 3;
// X88). One bordered box (the canon .tl-card): on the left the week ("19–25 Oct") as the date picker's button with
// Today beside it, then ‹, the seven day buttons Mon–Sun and ›. An arrow that can't go keeps its
// reason in reach: a tooltip on a mouse and a toast when pressed ("Past weeks aren't shown", "You can book up to 7
// days ahead"). On a phone the days scroll sideways between the arrows, snapping to a day, the chosen day scrolled
// into view. Under each day the free count for the group, or the viewer's own word ("At limit", "Sign in to book",
// "Not yet · opens Fri"); "Full" only when the day really is full; a day whose hours the club changed carries a
// marker (X88: the hours line over the grid is gone).
//
// X88 (Grant 2026-10-20: "use the same date picker as /book but enable past weeks" for the admin calendar; "could
// they not go on the left and have the whole thing in a border box and add a quick date picker to jump to a date"):
// `pastWeeks` opens past weeks and days (no lower limit); the date picker is bounded by jumpMin / jumpMax
// (yyyy-MM-dd, null for none; jumpWindow() in utils/bookingWeek).
//
// X91 (Grant 2026-10-21): both pages get every feature of the strip, none hidden by a page prop. "Today" shows
// whenever the day shown isn't today (another day of this week too, not only another week; it drops ?date=), and the
// admin calendar passes its week's counts as /book does. "Jump to a date and the date range button do the exact same
// thing … the date range button can just show the date picker immediately": the range (a calendar icon, the tooltip
// "Pick a date") opens the calendar in a popover on itself, and a picked day is shown at once (no dialog, no "Show
// this day"). On a phone ("the 'today' and quick date buttons are too small") the two are equal 44px halves on top of
// the days, Today | the range; Today is disabled, not hidden, on today, so nothing shifts. The ‹ › arrows stay at the
// ends of the days. A phone's count is an icon and the number (X91: "get rid of the word available").
//
// X195: a `side` slot for a page's own control on the chosen day (the admin calendar's day hours button), after the date
// control in the date side (flex 0 0 auto; the side keeps min-width: max-content, so the range never shrinks for it);
// under md the top line is Today | the range | it, the range keeping its old half. /book passes none.
import {computed, nextTick, onMounted, ref, watch} from 'vue';
import {useScrollCues} from '~/composables/useScrollCues';
import TapDatePickerCalendar from '~/components/fields/TapDatePickerCalendar.vue';
import {addDays} from '~/utils/bookingWeek';
import {pickerIsoForDay} from '~/utils/bookings';
import {meterWords} from '~/utils/bookingDayMeter';

const props = defineProps({
  /** dayStrip(): [{date, top, num, long, today, selected, past, beyond, changed, free, freeWord}] */
  days: {type: Array, default: () => []},
  weekLabel: {type: String, default: ''},
  /** weekNav(): {prev, next, prevHint, nextHint, isCurrent} */
  nav: {type: Object, default: () => ({})},
  /**
   * X88: past weeks and days are first-class (the admin calendar). The ‹ arrow always goes back a week (from the
   * nav's own prev, else the Monday before the days shown) and a past day is not dimmed. The member page leaves it
   * off, so its limits are the nav's (weekNav) as before.
   */
  pastWeeks: {type: Boolean, default: false},
  /** X88: the first and last day the date picker may pick (yyyy-MM-dd); null = no bound (X91: the days outside are greyed). */
  jumpMin: {type: String, default: null},
  jumpMax: {type: String, default: null},
});
const emit = defineEmits(['pickDay', 'pickWeek', 'pickDate', 'blocked']);

const daysEl = ref(null);
const reveal = async () => {
  await nextTick();
  const el = daysEl.value?.querySelector('.bds-day--on');
  if (el && daysEl.value && daysEl.value.scrollWidth > daysEl.value.clientWidth) el.scrollIntoView({block: 'nearest', inline: 'center'});
};
// X195 (Grant 2026-10-27: "The date tiles are very squished at around 1050px wide"): a day never gets narrower than
// MIN_DAY_W; when the seven don't fit, the days scroll inside the strip (snapping to a day, the chosen one in view,
// the cut ends faded), so a narrower content area shows fewer days instead of squashing them.
// Grant 2026-11-02: each end fades ONLY while there is more that way (useScrollCues, the same source as the booking
// grid's columns: scrollLeft against the overflow, on the first render, every scroll and a resize), never at the start
// or the end of the days, where the fade implied more to scroll when there wasn't.
const {cues} = useScrollCues(daysEl, {onResize: reveal});
onMounted(reveal);
watch(() => props.days.map((d) => d.selected).join(), reveal);

const arrow = (to, hint) => { if (to) emit('pickWeek', to); else emit('blocked', hint); };
const prevTo = computed(() => props.nav.prev || (props.pastWeeks && props.days[0]?.date ? addDays(props.days[0].date, -7) : null));
const prevHint = computed(() => (prevTo.value && !props.nav.prev ? 'Previous week' : props.nav.prevHint || 'Previous week'));
// X91: on today already (the day shown is today), Today has nothing to do.
const onToday = computed(() => props.days.some((d) => d.selected && d.today));
const goToday = () => { if (!onToday.value) emit('pickWeek', null); };
// X195 (Grant 2026-10-27: "The 'X available' under each date make it look really busy when it ellipsises"): under the
// date a small availability bar (utils/bookingDayMeter: plenty / getting busy / fully booked, colour and fill), the
// word "Closed" for a day closed all day, else the short form ("Opens Fri", the lock for Can't book), never cut; the
// exact words are in the tooltip and the label ("Saturday 31 October · 12 slots available", "Fully booked").
const dayWords = (d) => (d.meter ? meterWords(d.meter, d.free) : d.freeWord);
const dayTip = (d) => {
  // X91: the day's word too, since the button shows only the icon and the number.
  const base = d.beyond ? `${d.long}: opens for booking later. You can look, but not book yet.` : dayWords(d) ? `${d.long} · ${dayWords(d)}` : d.long;
  return d.changed ? `${base}. The club changed the hours for this day.` : base;
};

// ── Pick a date (X91, Grant 2026-10-21: "the date range button can just show the date picker immediately"): the
// range is the button; it opens the kit's calendar card (TapDatePickerCalendar) in a popover on itself, and a picked
// day is shown at once (the page's pickDate: router.replace ?date=). Days outside jumpMin / jumpMax are greyed in the
// calendar (a member's window); an admin passes none.
const pickOpen = ref(false);
const selectedDate = computed(() => props.days.find((d) => d.selected)?.date ?? null);
const selectedIso = computed(() => (selectedDate.value ? pickerIsoForDay(selectedDate.value) : null));
const pickerMin = computed(() => (props.jumpMin ? pickerIsoForDay(props.jumpMin) : ''));
const pickerMax = computed(() => (props.jumpMax ? pickerIsoForDay(props.jumpMax) : undefined));
const onPick = (day) => {
  pickOpen.value = false;
  const date = day?.toISODate?.();
  if (date) emit('pickDate', date);
};
</script>

<template>
  <div class="tl-card bds">
    <!-- X91: the range is the date picker's button (its calendar opens on it, a pick shows that day), Today beside it;
         on a phone the two are equal halves on top of the days: Today | the range -->
    <div class="bds-side" :class="{'bds-side--extra': !!$slots.side}">
      <v-menu v-model="pickOpen" :close-on-content-click="false" location="bottom start" :offset="6" :min-width="0" content-class="tdp-menu">
        <template #activator="{props: menuProps}">
          <v-btn v-bind="menuProps" variant="outlined" rounded="lg" class="bds-range" prepend-icon="mdi-calendar-month-outline" :aria-label="weekLabel ? `Pick a date (week of ${weekLabel})` : 'Pick a date'">
            {{ weekLabel }}
            <v-tooltip activator="parent" location="top" text="Pick a date"/>
          </v-btn>
        </template>
        <tap-date-picker-calendar :selected-iso="selectedIso" :min="pickerMin" :max="pickerMax" :quick="[]" :open="pickOpen" @pick="onPick"/>
      </v-menu>
      <v-btn variant="outlined" rounded="lg" class="bds-today" :disabled="onToday" @click="goToday">Today</v-btn>
      <!-- X195: a page's own control for the chosen day (the admin calendar's day hours) after the date control; under
           md the third part of the top line (Today | the range | it), the range keeping its old half -->
      <div v-if="$slots.side" class="bds-extra"><slot name="side"/></div>
    </div>
    <v-tooltip :text="prevHint" location="top">
      <template #activator="{props: tip}">
        <span v-bind="tip" class="bds-arrow-wrap">
          <button type="button" class="bds-arrow" :class="{'bds-arrow--off': !prevTo}" :aria-label="prevHint" :aria-disabled="!prevTo || undefined" @click="arrow(prevTo, prevHint)">
            <v-icon size="20" icon="mdi-chevron-left"/>
          </button>
        </span>
      </template>
    </v-tooltip>
    <div ref="daysEl" class="bds-days" :class="{'bds-days--scroll': cues.left || cues.right, 'bds-days--more-l': cues.left, 'bds-days--more-r': cues.right}" role="tablist" :aria-label="weekLabel ? `Day, week of ${weekLabel}` : 'Day'">
      <v-tooltip v-for="d in days" :key="d.date" :text="dayTip(d)" location="bottom">
        <template #activator="{props: tip}">
          <button v-bind="tip" type="button" role="tab" class="bds-day" :aria-selected="d.selected"
                  :class="{'bds-day--on': d.selected, 'bds-day--today': d.today, 'bds-day--past': d.past, 'bds-day--beyond': d.beyond, 'bds-day--changed': d.changed}"
                  @click="$emit('pickDay', d.date)">
            <span class="bds-day-top">{{ d.today ? 'Today' : d.top }}</span>
            <span class="bds-day-num">{{ d.num }}</span>
            <span class="bds-day-free" :class="{'bds-day-free--off': d.free === 0 || d.beyond || d.past}">
              <v-icon v-if="d.beyond" size="10" icon="mdi-lock-clock"/><v-icon v-else-if="d.changed" size="10" icon="mdi-calendar-edit" class="bds-day-changed"/>
              <!-- Grant 2026-11-02: the week isn't here yet: a skeleton bar where the count goes, never a day word -->
              <span v-if="d.pending" class="bds-meter bds-meter--pending" aria-hidden="true"/>
              <span v-else-if="d.meter?.bar" class="bds-meter" :class="`bds-meter--${d.meter.level}`" :style="{'--fill': d.meter.fill}" role="img" :aria-label="dayWords(d)"><span class="bds-meter-fill"/></span>
              <span v-else-if="d.meter" class="bds-meter-word">{{ d.meter.word }}</span>
              <span v-else-if="d.freeShort != null || d.phoneIcon" class="bds-free-short" :aria-label="dayWords(d)"><v-icon v-if="d.phoneIcon" size="11" :icon="d.phoneIcon" class="bds-free-icon" aria-hidden="true"/>{{ d.freeShort }}</span>
              <span v-else>{{ d.freeWord || (d.changed ? 'Changed' : '') }}</span>
            </span>
          </button>
        </template>
      </v-tooltip>
    </div>
    <v-tooltip :text="nav.nextHint || 'Next week'" location="top">
      <template #activator="{props: tip}">
        <span v-bind="tip" class="bds-arrow-wrap">
          <button type="button" class="bds-arrow" :class="{'bds-arrow--off': !nav.next}" :aria-label="nav.nextHint || 'Next week'" :aria-disabled="!nav.next || undefined" @click="arrow(nav.next, nav.nextHint)">
            <v-icon size="20" icon="mdi-chevron-right"/>
          </button>
        </span>
      </template>
    </v-tooltip>

  </div>
</template>

<style scoped lang="scss">
/* X88: one bordered box (.tl-card's border and radius); overflow stays visible to the focus rings, the days scroll. */
.bds { display: flex; align-items: stretch; gap: 8px; padding: 8px; overflow: visible; }
.bds-side { display: flex; align-items: center; gap: 6px; flex: 0 0 auto; min-width: max-content; padding: 0 2px 0 4px; }
/* X195: a page's control after the date control: its own width, never taken from the date control. */
.bds-extra { display: flex; align-items: center; flex: 0 0 auto; }
/* X91: the range (the date picker's button) and Today: the kit's outlined buttons, one height. */
.bds-range, .bds-today {
  height: 44px !important; padding: 0 12px; letter-spacing: 0; text-transform: none; font-weight: 700;
  border-color: rgb(var(--v-theme-borderLight)); color: rgb(var(--v-theme-text));
}
.bds-range { font-size: 14px; letter-spacing: -0.2px; :deep(.v-btn__prepend) { margin-inline-end: 6px; color: rgb(var(--v-theme-primary)); } }
.bds-today { font-size: 12.5px; color: rgb(var(--v-theme-primary)); }
.bds-arrow-wrap { display: flex; flex: 0 0 auto; }
.bds-arrow {
  display: flex; align-items: center; justify-content: center; width: 36px; height: 100%; min-height: 64px; border-radius: 12px;
  border: 1px solid rgb(var(--v-theme-borderLight)); background: rgb(var(--v-theme-surface)); color: rgb(var(--v-theme-text)); cursor: pointer; font: inherit;
  &:hover { background: rgb(var(--v-theme-light)); }
  &--off { color: rgb(var(--v-theme-borderLight)); cursor: not-allowed; &:hover { background: rgb(var(--v-theme-surface)); } }
  &:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 1px; }
}
.bds-days {
  display: grid; grid-template-columns: repeat(7, minmax(88px, 1fr)); gap: 6px; flex: 1 1 auto; min-width: 0;
  /* X195: never narrower than 88px a day (their width at 1440 is ~100px); fewer days show and the rest scroll. */
  overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; scrollbar-width: none; padding: 1px;
  &::-webkit-scrollbar { display: none; }
  /* Grant 2026-11-02: the fade per end, 14px only on an end with more to scroll (--more-l / --more-r), else 0 (no fade). */
  &--scroll {
    --bds-fade-l: 0px;
    --bds-fade-r: 0px;
    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 var(--bds-fade-l), #000 calc(100% - var(--bds-fade-r)), transparent 100%);
    mask-image: linear-gradient(to right, transparent 0, #000 var(--bds-fade-l), #000 calc(100% - var(--bds-fade-r)), transparent 100%);
    scroll-padding-inline: 14px;
  }
  &--more-l { --bds-fade-l: 14px; }
  &--more-r { --bds-fade-r: 14px; }
}
.bds-day {
  display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 8px 4px 7px; border-radius: 12px; scroll-snap-align: center;
  border: 1px solid rgb(var(--v-theme-borderLight)); background: rgb(var(--v-theme-surface)); font-family: inherit; cursor: pointer; min-width: 0;
  &:hover { background: rgb(var(--v-theme-light)); }
  &--today .bds-day-top { color: rgb(var(--v-theme-primary)); }
  &--on { border-color: rgb(var(--v-theme-primary)); background: rgb(var(--v-theme-brandTint)) !important; .bds-day-num, .bds-day-top { color: rgb(var(--v-theme-primary)); } }
  &--past { opacity: .5; }
  &--beyond { border-style: dashed; .bds-day-num { color: rgb(var(--v-theme-charcoal)); } }
  &:focus-visible { outline: 2px solid rgb(var(--v-theme-primary)); outline-offset: 1px; }
}
.bds-day-top { font-size: 11px; font-weight: 600; color: rgb(var(--v-theme-textSubtle)); letter-spacing: .2px; }
.bds-day-num { font-size: 17px; font-weight: 800; letter-spacing: -0.3px; line-height: 1.1; }
.bds-day-free {
  display: inline-flex; align-items: center; gap: 3px; font-size: 10.5px; font-weight: 600; color: rgb(var(--v-theme-success)); white-space: nowrap; max-width: 100%; min-height: 14px;
  &--off { color: rgb(var(--v-theme-textSubtle)); }
  .bds-day--past & { color: rgb(var(--v-theme-textSubtle)); }
}
.bds-day-changed { color: rgb(var(--v-theme-warning)); }
.bds-day-free > span { min-width: 0; }
/* X195: the short form at every width (it was the phone's only); the long words live in the tooltip and label. */
.bds-free-short { display: inline-flex; align-items: center; gap: 2px; }
/* X195: the availability bar: one size on every day, colour and fill (full / a third / the empty outline). */
.bds-meter {
  display: inline-block; box-sizing: border-box; width: 28px; height: 6px; border-radius: 3px; overflow: hidden;
  border: 1px solid currentColor; flex: 0 0 auto;
  &--plenty { color: rgb(var(--v-theme-success)); }
  &--few { color: rgb(var(--v-theme-warning)); }
  &--full { color: rgb(var(--v-theme-error)); }
}
.bds-meter-fill { display: block; height: 100%; width: calc(var(--fill, 0) * 100%); background: currentColor; }
.bds-meter-word { color: rgb(var(--v-theme-textSubtle)); }
/* Grant 2026-11-02: the day's data is still loading: the meter's size in the kit's skeleton grey (settings.scss), no outline. */
.bds-meter--pending { border-color: transparent; background: #ECECEC; }
.bds-day--changed .bds-day-free { color: rgb(var(--v-theme-charcoal)); }

/* X91 (Grant 2026-10-21: "the break point for when the date picker and today needs to go on the top needs to be at md
   not sm"): under Vuetify's md (smAndDown), Today | the range as two equal halves on top, full width; then ‹ days › on
   the next line. From md up, one row with the range on the left. */
@media (max-width: 959.98px) {
  .bds { flex-wrap: wrap; gap: 8px 6px; }
  .bds-side { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; flex: 0 0 100%; min-width: 0; padding: 0; }
  .bds-today { order: -1; }
  /* X195 (Grant: "space above it in thirds next to Today — Date picker — Change hours"): with a slotted control the top
     line is Today | the range | it; the range keeps exactly the half it had (calc(50% - 3px), the 6px gap's half), the
     other two share the rest. */
  .bds-side--extra { grid-template-columns: minmax(0, 1fr) calc(50% - 3px) minmax(0, 1fr); }
  .bds-extra { min-width: 0; }
  .bds-range, .bds-today { width: 100%; min-width: 0; padding: 0 8px; :deep(.v-btn__content) { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } }
  .bds-days { flex: 1 1 0; }
}

@media (max-width: 640px) {
  .bds { padding: 6px; gap: 6px 4px; }
  .bds-range { font-size: 13px; }
  .bds-arrow { width: 28px; min-height: 60px; border-radius: 10px; }
  /* The days scroll between the arrows and snap to a day; the chosen one is scrolled into view. */
  /* X94 (R35 L5): a day cut at either end fades out instead of looking like it sits under the ‹ › arrows.
     Grant 2026-11-02: the fade comes from the --scroll / --more-l / --more-r classes above, per end and only while there
     is more that way. */
  .bds-days {
    display: flex; gap: 4px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; scrollbar-width: none; padding: 1px;
    &::-webkit-scrollbar { display: none; }
    scroll-padding-inline: 14px;
  }
  .bds-day { position: relative; flex: 0 0 auto; width: 64px; scroll-snap-align: center; padding: 6px 2px 5px; border-radius: 10px; }
  /* X89 (R33 F4): the day's marker (changed, opens later) moves to the button's top corner, so the word under the
     number has the whole width ("Can't book", "Opens Fri") and is never cut to "Can't b…". */
  .bds-day-free > .v-icon { position: absolute; top: 4px; right: 4px; }
  .bds-day-num { font-size: 15px; }
  .bds-day-top { font-size: 10px; }
  .bds-day-free { font-size: 9.5px; }
}
</style>
